/** Формулы плана производства — дословный порт клиентской логики оригинала
 * (build-скрипт): календарь недель (L6197), литраж (L6447/6490), котлы (L6503/6543),
 * предупреждения (L6686), вид/ранг (L6463+), покрытие (L6940). Python-близнец
 * серверных частей — orders/production_calc.py (менять синхронно). */
import type { ArticleAttrs } from "@/api/orders-planning";

// ── календарь ──

export interface ProductionWeek {
  key: string;   // "2026-W12" без ведущего нуля
  month: string; // месяц даты начала недели
  index: number;
  from: string;  // YYYY-MM-DD
  to: string;
  range: string; // "01.01-07.01"
}

const pad2 = (n: number): string => String(n).padStart(2, "0");
const iso = (d: Date): string => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** Порт productionYearWeeks: НЕ ISO — 7-дневки от 1 января, последняя обрезается. */
export function productionYearWeeks(year: number): ProductionWeek[] {
  const weeks: ProductionWeek[] = [];
  const end = new Date(year, 11, 31);
  const cursor = new Date(year, 0, 1);
  let index = 1;
  while (cursor <= end) {
    const from = new Date(cursor);
    const to = new Date(cursor);
    to.setDate(to.getDate() + 6);
    if (to > end) to.setTime(end.getTime());
    weeks.push({
      key: `${year}-W${index}`,
      month: `${from.getFullYear()}-${pad2(from.getMonth() + 1)}`,
      index,
      from: iso(from),
      to: iso(to),
      range: `${pad2(from.getDate())}.${pad2(from.getMonth() + 1)}-${pad2(to.getDate())}.${pad2(to.getMonth() + 1)}`,
    });
    cursor.setDate(cursor.getDate() + 7);
    index += 1;
  }
  return weeks;
}

/** Порт currentProductionWeekIndex: прошлый год → 999, будущий → 1. */
export function currentProductionWeekIndex(year: number, today = new Date()): number {
  if (year < today.getFullYear()) return 999;
  if (year > today.getFullYear()) return 1;
  const start = new Date(year, 0, 1);
  return Math.floor((today.getTime() - start.getTime()) / 86400000 / 7) + 1;
}

export function productionWeekIndex(weekKey: string): number {
  const m = /-W(\d+)$/.exec(weekKey);
  return m ? Number(m[1]) : 0;
}

export function productionYearForWeek(weekKey: string): number {
  return Number(weekKey.slice(0, 4));
}

// ── литраж ──

/** Порт articleBottleMl: ручной volume_ml, иначе парсинг «N л» (×1000) или «NN мл». */
export function articleBottleMl(
  article: string,
  name: string,
  attrs: ArticleAttrs | undefined,
): number {
  const manual = attrs?.volume_ml ? Math.max(0, Math.round(attrs.volume_ml)) : 0;
  if (manual > 0) return manual;
  const text = [article, name, attrs?.product_type ?? ""].join(" ").toLowerCase();
  // оригинал использовал `л\b`, но JS \b не работает после кириллицы — ветка «N л»
  // никогда не срабатывала (скрытый баг). Фиксим lookahead'ом, поведение «N мл» прежнее.
  const liters = /(\d+(?:[,.]\d+)?)\s*л(?=[\s,.;)!»"']|$)/.exec(text);
  if (liters) return Math.round(Number(liters[1]!.replace(",", ".")) * 1000);
  const ml = /(\d{2,4})\s*мл/.exec(text);
  if (ml) return Number(ml[1]);
  return 0;
}

export function articleBottleMlIsManual(attrs: ArticleAttrs | undefined): boolean {
  return !!attrs?.volume_ml && attrs.volume_ml > 0;
}

/** Порт productionLiters: qty × мл / 1000, округление до 2 знаков. */
export function productionLiters(ml: number, qty: number): number {
  return ml ? Math.round((qty * ml) / 10) / 100 : 0;
}

// ── котлы ──

export interface BatchPlan {
  name: string;
  batches: number;
  capacity: number;
  waste: number;
  note: string;
  warning: string;
  inefficientBig: boolean;
}

const batchCache = new Map<number, BatchPlan>();

/** Порт productionBatchPlan: перебор 220/1000/3000 л, score = отходы + 60·варка +
 * штрафы недогруза 3-тонника (<70% жёсткий 10000, <85% мягкий 1500). Мемоизировано. */
export function productionBatchPlan(liters: number): BatchPlan {
  const value = Math.round(liters * 100) / 100;
  const cached = batchCache.get(value);
  if (cached) return cached;
  if (value === 0) {
    const empty: BatchPlan = {
      name: "-", batches: 0, capacity: 0, waste: 0, note: "Введите план", warning: "",
      inefficientBig: false,
    };
    batchCache.set(0, empty);
    return empty;
  }
  const maxBig = Math.ceil(value / 3000) + 1;
  const maxTon = Math.ceil(value / 1000) + 2;
  const maxSmall = Math.ceil(value / 220) + 2;
  let best: { score: number; big: number; ton: number; small: number; capacity: number; waste: number } | null =
    null;
  for (let big = 0; big <= maxBig; big += 1) {
    for (let ton = 0; ton <= maxTon; ton += 1) {
      for (let small = 0; small <= maxSmall; small += 1) {
        const batches = big + ton + small;
        if (!batches) continue;
        const capacity = big * 3000 + ton * 1000 + small * 220;
        if (capacity < value) continue;
        const waste = capacity - value;
        const bigLoad = big ? value / (big * 3000) : 1;
        const inefficientBig = big > 0 && bigLoad < 0.7;
        let score = waste + batches * 60;
        if (inefficientBig) score += 10000;
        if (big > 0 && bigLoad < 0.85) score += 1500;
        if (!best || score < best.score) best = { score, big, ton, small, capacity, waste };
        if (capacity >= value + 3000) break;
      }
    }
  }
  const b = best!;
  const parts: string[] = [];
  if (b.ton) parts.push(`${b.ton} × тонник`);
  if (b.small) parts.push(`${b.small} × малый 220 л`);
  if (b.big) parts.push(`${b.big} × 3-тонник`);
  const name = parts.join(" + ");
  const bigLoad = b.big ? value / (b.big * 3000) : 1;
  const inefficient = b.big > 0 && bigLoad < 0.7;
  const plan: BatchPlan = {
    name,
    batches: b.big + b.ton + b.small,
    capacity: b.capacity,
    waste: Math.round(b.waste * 100) / 100,
    note: `план варок: ${name}; ёмкость ${b.capacity} л, свободно ${Math.round(b.waste * 100) / 100} л`,
    warning: inefficient
      ? "3-тонник загружен меньше 70%, лучше разделить или перенести часть объёма."
      : "",
    inefficientBig: inefficient,
  };
  batchCache.set(value, plan);
  return plan;
}

// ── вид продукта, ранги, предупреждения ──

/** Порт productionKind: сохранённый тип, иначе эвристика по названию. */
export function productionKind(name: string, attrs: ArticleAttrs | undefined): string {
  const saved = attrs?.product_type?.trim();
  if (saved) return saved.toLowerCase();
  const text = name.toLowerCase();
  if (/спре/.test(text)) return "спрей";
  if (/дез/.test(text)) return "дезодорант";
  if (/гель|шамп/.test(text)) return "гель";
  if (/крем/.test(text)) return "крем";
  if (/молоч/.test(text)) return "молочко";
  if (/набор/.test(text)) return "набор";
  if (/новин/.test(text)) return "новинка";
  return "Не задано";
}

/** Порт productionKindRank. */
export function productionKindRank(kind: string): number {
  const k = kind.toLowerCase();
  if (/спре/.test(k)) return 1;
  if (/дез/.test(k)) return 2;
  if (/гель|шамп/.test(k)) return 3;
  if (/крем/.test(k)) return 4;
  if (/молоч/.test(k)) return 5;
  if (/набор/.test(k)) return 6;
  if (/новин/.test(k)) return 7;
  return 8;
}

/** Порт productionOrderRank: kindRank·100000 + (ml || 9999)·10 — базовая сортировка. */
export function productionOrderRank(kind: string, ml: number): number {
  return productionKindRank(kind) * 100000 + (ml || 9999) * 10;
}

/** Порт productionWarnings (с исправленным мёртвым сравнением «Трёхтонник»:
 * проверка новинки идёт по наличию 3-тонника в подборе). */
export function productionWarnings(
  name: string,
  attrs: ArticleAttrs | undefined,
  ml: number,
  qty: number,
): string[] {
  const warnings: string[] = [];
  if (!attrs?.product_type) warnings.push("Заполнить тип продукта");
  if (ml === 0) warnings.push("Не найден объём");
  else if (!articleBottleMlIsManual(attrs)) warnings.push("Объём взят из названия");
  const kind = productionKind(name, attrs);
  const kettle = productionBatchPlan(productionLiters(ml, qty));
  if (/новин/.test(kind) && kettle.name.includes("3-тонник")) {
    warnings.push("Новинку не ставить в трёхтонник");
  }
  if (kettle.warning) warnings.push(kettle.warning);
  if (kettle.batches > 1) warnings.push(`Нужно дробление: ${kettle.batches} вар.`);
  if (/крем|молоч/.test(kind)) warnings.push("После крема нужен гель, не спрей");
  if (/набор/.test(name.toLowerCase())) warnings.push("Проверить комплектность набора");
  return warnings;
}

// ── план: display value ──

export interface ProductionCellData {
  article: string;
  week_key: string;
  channel_code: string;
  qty: number;
}

/** Порт productionDisplayValue: Σ(каналов) либо legacy ''-строка. */
export function displayValue(cells: ProductionCellData[]): number {
  const channels = cells.filter((c) => c.channel_code !== "");
  const sum = channels.reduce((s, c) => s + c.qty, 0);
  if (sum > 0) return sum;
  return cells.find((c) => c.channel_code === "")?.qty ?? 0;
}

// ── покрытие ──

export type CoverageStatus = "ok" | "under" | "over" | "urgent" | "check";

export interface CoverageSignal {
  forecast: number;
  dynamic: number;
  base: number;
  planned: number;
  stock: number;
  coverage: number;
  need: number;
  deviation: number;
  deviationPct: number;
  status: CoverageStatus;
  label: string;
  note: string;
  stockDays: number | null;
}

/** Порт productionCoverageSignal (стр. 6940-6975): каскад статусов с перекрывающим блоком. */
export function productionCoverageSignal(args: {
  days: number;          // дней в плановом периоде
  recentQty: number;     // продажи за 90 дней (якорь = min(bounds.to, max_date))
  last30Qty: number;     // продажи за 30 дней
  forecast: number;      // прогноз продаж (план по всем каналам за месяцы периода)
  planned: number;       // план производства по видимым неделям
  stockTotal: number;
}): CoverageSignal {
  const { days, recentQty, last30Qty, forecast, planned, stockTotal } = args;
  const dailyDemand = Math.max(recentQty / 90, last30Qty / 30);
  const dynamic = Math.round(dailyDemand * days);
  const base = Math.max(forecast, dynamic);
  const stock = stockTotal;
  const coverage = stock + planned;
  const need = Math.max(0, base - stock);
  const deviation = coverage - base;
  const deviationPct = base ? Math.round((deviation / base) * 100) : coverage ? 100 : 0;
  const stockDays = dailyDemand > 0 ? Math.round(stock / dailyDemand) : stock > 0 ? null : 0;

  let status: CoverageStatus = "ok";
  let label = "OK";
  let note = "Остаток + план пр-ва покрывают прогноз/динамику.";
  if (base <= 0 && planned > 0) {
    status = "over";
    label = "Переплан";
    note = "Есть пр-во, но нет прогноза и спроса.";
  } else if (base > 0 && deviation < -Math.max(100, base * 0.15)) {
    status = "under";
    label = "Недоплан";
    note = "Покрытие ниже ориентира: нужно допланировать.";
  } else if (base > 0 && deviation > Math.max(100, base * 0.3) && planned > 0) {
    status = "over";
    label = "Переплан";
    note = "Пр-во + остаток сильно выше прогноза/динамики.";
  }
  // перекрывающий блок
  if (base > 0 && stockDays !== null && stockDays <= 30 && deviation < 0) {
    status = "urgent";
    label = "Срочно";
    note = "Запас меньше 30 дней, а покрытия не хватает.";
  } else if (forecast > 0 && dynamic > forecast * 1.3) {
    status = "check";
    label = "Факт > прогноз";
    note = "Динамика выше согласованного прогноза: проверьте план.";
  } else if (forecast > 0 && dynamic < forecast * 0.7 && coverage >= base) {
    status = "check";
    label = "Факт < прогноз";
    note = "Динамика ниже прогноза: не ставьте слепо.";
  }
  return {
    forecast, dynamic, base, planned, stock, coverage, need,
    deviation, deviationPct, status, label, note, stockDays,
  };
}
